/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, Alert, Empty, Tag } from "antd";
import { TablePaginationConfig } from "antd/es/table";
import dayjs from "dayjs";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheckCircle, faBan } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetServersQuery,
  usePostSecretClientTokenMutation,
} from "../../../redux/servers/serverAPI";
import { useGetOrganizationsLightQuery } from "../../../redux/organizations/organizationAPI";
import { updateOpenModalSecretClient } from "../../../redux/servers/serverSlice";
import { updateTotpTokenIsValid } from "../../../redux/authentication/authenticationSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
import AuthenticationTOTP from "../../authentication/AuthenticationTOTP";
import ServerModalSecretClient from "./ServerModalSecretClient";
import ServersTableActions from "./ServersTableActions";
import FilterSearchText from "../../components/filters/FilterSearchText";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
import { OganizationLightInterface } from "../../../interfaces/Organizations.interface";
// CONSTANTS
import { BLANK_SERVER } from "../../../constants/server";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";

const ServersTable = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { Column } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [servers, setServers] = useState<ServerInterface[]>([]);
  const [server, setServer] = useState<ServerInterface>({ ...BLANK_SERVER });
  const [secretClientToken, setSecretClientToken] = useState("");
  const [tokenOrganizations, setTokenOrganizations] = useState<string | null>(null);
  const [organizationNamesById, setOrganizationNamesById] = useState<Record<string, string>>({});
  const [filterName, setFilterName] = useState("");
  const [filterIP, setFilterIP] = useState("");
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { totpTokenIsValid, totpToken } = useSelector((state: RootState) => state.authentication);
  const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const { serverRefreshTable, server: selectedServer } = useSelector(
    (state: RootState) => state.server,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetServersTable, { data, isLoading, isError, isFetching }] =
    useLazyGetServersQuery();
  const [
    triggerGetSecretClientMutation,
    {
      data: dataSecretClientToken,
      isLoading: isLoadingSecretClientToken,
      isSuccess: isSuccessSecretClientToken,
      isError: isErrorSecretClientToken,
      error: errorSecretClientToken,
    },
  ] = usePostSecretClientTokenMutation();
  const { data: dataOrganizationsLight, refetch: refetchOrganizations } =
    useGetOrganizationsLightQuery({ token: tokenOrganizations }, { skip: !tokenOrganizations });

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getData = async (page: number, limit: number) => {
    let idOrganization = globalOrganizationId;
    if (userLogged && userLogged.organizationId && userLogged.organizationId === idOrganization) {
      idOrganization = "";
    }
    const searchAndFilterString = buildFilterString({
      filterName,
      filterIP,
      filterOrganizationName: idOrganization,
    });
    const token = await GETJwtToken();
    triggerGetServersTable({
      page,
      limit,
      token,
      searchAndFilterString,
    }).unwrap();
  };

  const handleTableChange = (newValue: TablePaginationConfig) => {
    setPagination({
      ...pagination,
      current: newValue.current,
      pageSize: newValue.pageSize,
      total: newValue.total,
    });
    getData(newValue.current || 1, newValue.pageSize || 10);
  };

  const TotpTokenIsValid = async () => {
    if (totpTokenIsValid) {
      const token = await GETJwtToken();
      triggerGetSecretClientMutation({
        token,
        body: {
          server_id: formatUuidT(server.id),
          totpToken,
        },
      });
    }
  };

  const refreshTable = async () => {
    setPagination({ ...pagination, current: 1, pageSize: 10 });
    getData(1, 10);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (data && data.data) {
      setPagination({
        ...pagination,
        total: data.total,
      });
      setServers(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      setServers([]);
    }
  }, [isError]);

  useEffect(() => {
    if (serverRefreshTable) {
      getData(pagination.current || 1, pagination.pageSize || 10);
      if (tokenOrganizations) {
        refetchOrganizations();
      }
    }
  }, [serverRefreshTable, tokenOrganizations]);

  useEffect(() => {
    if (totpTokenIsValid) {
      TotpTokenIsValid();
      setTimeout(() => {
        dispatch(updateTotpTokenIsValid(false));
      }, 1000);
    }
  }, [totpTokenIsValid]);

  useEffect(() => {
    if (dataSecretClientToken && dataSecretClientToken.data && dataSecretClientToken.data.Secret) {
      setSecretClientToken(dataSecretClientToken.data.Secret);
      dispatch(updateOpenModalSecretClient(true));
    }
  }, [dataSecretClientToken]);

  useEffect(() => {
    if (globalOrganizationId !== "") {
      refreshTable();
    }
  }, [globalOrganizationId]);

  useEffect(() => {
    if (selectedServer && selectedServer.id !== null) {
      setServer({ ...selectedServer });
    }
  }, [selectedServer]);

  useEffect(() => {
    Promise.resolve(GETJwtToken()).then(setTokenOrganizations);
  }, []);

  useEffect(() => {
    if (!dataOrganizationsLight?.data) return;

    const organizationsMap: Record<string, string> = {};
    dataOrganizationsLight.data.forEach((organization: OganizationLightInterface) => {
      if (!organization.id) return;
      const id = formatUuidT(organization.id);
      const name = String(organization.name ?? "").trim();
      if (id && name) {
        organizationsMap[id] = name;
      }
    });

    setOrganizationNamesById(organizationsMap);
  }, [dataOrganizationsLight]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      {hasPermission(Permission.ServersView, permissionSet ?? []) ? (
        <>
          {!isError ? (
            <Table
              rowClassName={(record, index) => getTableRowClass(index, theme)}
              dataSource={servers}
              loading={isLoading || isFetching || isLoadingSecretClientToken}
              className="my-custom-table"
              rowKey={(record) => formatUuidT(record.id)}
              size="small"
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 900 }}
              footer={() => (
                <div className="generalStyles__flexEnd">
                  <Tag>{pagination.total} Rows</Tag>
                </div>
              )}
            >
              {/** NAME */}
              <Column
                title={t("general.name")}
                dataIndex="name"
                key="name"
                filterIcon={<FontAwesomeIcon icon={faSearch} />}
                filterDropdown={() => (
                  <FilterSearchText
                    placeHolder={t("general.name")}
                    filterValue={filterName}
                    setFilterValue={setFilterName}
                    refreshTable={refreshTable}
                    refreshParameters={[]}
                  />
                )}
                render={(text: string | Uint8Array | null) => <div>{String(text ?? "")}</div>}
              />
              {/** IP */}
              <Column
                title={t("edgeManagement.servers.ip")}
                dataIndex="ip"
                key="ip"
                filterIcon={<FontAwesomeIcon icon={faSearch} />}
                filterDropdown={() => (
                  <FilterSearchText
                    placeHolder={t("edgeManagement.servers.ip")}
                    filterValue={filterIP}
                    setFilterValue={setFilterIP}
                    refreshTable={refreshTable}
                    refreshParameters={[]}
                  />
                )}
                render={(text: string | Uint8Array | null) => <div>{String(text ?? "")}</div>}
              />
              {/** ORGANIZATION */}
              <Column
                title={t("general.organization")}
                key="org"
                render={(_: any, record: ServerInterface) => {
                  if (!record.orgId) {
                    return <Tag>{t("general.noAssigned")}</Tag>;
                  }

                  const orgId = formatUuidT(record.orgId);
                  const orgName = organizationNamesById[orgId];
                  return <div>{orgName || orgId}</div>;
                }}
              />

              {/** TOTAL CHANNELS ASSIGNED */}
              <Column
                width="150px"
                title={t("edgeManagement.channels.channels")}
                dataIndex="channels"
                key="channels"
                render={(text, record: ServerInterface) => {
                  const count =
                    record?.channelCount ?? (record?.channels ? record.channels.length : 0);
                  return (
                    <>
                      <div>{count}</div>
                    </>
                  );
                }}
              />
              {/** STATUS */}
              <Column
                title={t("general.active")}
                dataIndex="active"
                key="active"
                width="130px"
                render={(value: boolean) =>
                  value ? (
                    <Tag
                      icon={
                        <FontAwesomeIcon icon={faCheckCircle} className="generalStyles__mrFix" />
                      }
                      className="generalStyles__width100px generalStyles__textCenter"
                      color="green"
                    >
                      {t("general.active")}
                    </Tag>
                  ) : (
                    <Tag
                      icon={<FontAwesomeIcon icon={faBan} className="generalStyles__mrFix" />}
                      className="generalStyles__width100px generalStyles__textCenter"
                      color="red"
                    >
                      {t("general.inactive")}
                    </Tag>
                  )
                }
              />
              {/** CREATED AT */}
              <Column
                title={t("general.createdAt")}
                dataIndex="createdAt"
                key="createdAt"
                width="180px"
                render={(text: string | Uint8Array | null) => {
                  const value = String(text ?? "").trim();
                  if (!value) {
                    return <Tag>--</Tag>;
                  }

                  const formatted = dayjs(value).isValid()
                    ? dayjs(value).format("YYYY-MM-DD HH:mm")
                    : value;

                  return <Tag color="blue">{formatted}</Tag>;
                }}
              />
              {/** ACTIONS */}
              <Column
                align="center"
                title={t("edgeManagement.banks.actions")}
                key="action"
                onCell={() => ({
                  onClick: (event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                  },
                })}
                render={(_: any, record: ServerInterface) => (
                  <ServersTableActions selectedItem={record} />
                )}
              />
            </Table>
          ) : (
            <Alert
              message={t("organizationManagement.errorDescription.error")}
              description={t("organizationManagement.errorDescription.text1")}
              type="error"
              showIcon
              className="mt-3"
            />
          )}
        </>
      ) : (
        <>
          <div className="generalStyles__noAccessToListTable">
            <Empty
              description={t("edgeManagement.servers.listNotAvailable")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        </>
      )}
      {/** ********************************************* */}
      {/** AUTHENTICATION TOPTP TOKEN */}
      <AuthenticationTOTP />
      {/** ********************************************* */}
      {/** SECRET CLIENT */}
      <ServerModalSecretClient secretClientToken={secretClientToken} />
      {/** ********************************************* */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorSecretClientToken}
        isSuccess={isSuccessSecretClientToken}
        requestType="POST"
        error={errorSecretClientToken}
        name="Client Secret"
      />
    </div>
  );
};

export default ServersTable;
